import { BTLocalStorage } from "types/btStorage";

const WHATS_NEW_LAST_SEEN_STORAGE_KEY = "bt-object-whatsNewLastSeen" as const;

function buildKey(builderId: number, userId: number): string {
    return `${builderId}:${userId}`;
}

/**
 * The newest announcement date this person has seen on this device, or null if they never opened
 * the panel here. Device-local, so clearing site data brings the dot back.
 */
export function getWhatsNewLastSeen(builderId: number, userId: number | null): number | null {
    if (userId == null) return null;

    const map = BTLocalStorage.get(WHATS_NEW_LAST_SEEN_STORAGE_KEY);
    return map?.[buildKey(builderId, userId)] ?? null;
}

/**
 * Marks everything up to `publishedDateUtc` (the date the panel shows) as seen. Stored as epoch ms
 * because the storage wrapper throws on `Date`.
 */
export function setWhatsNewLastSeen(
    builderId: number,
    userId: number | null,
    publishedDateUtc: Date | null | undefined,
): void {
    if (userId == null || publishedDateUtc == null) return;

    const map = BTLocalStorage.get(WHATS_NEW_LAST_SEEN_STORAGE_KEY) ?? {};
    BTLocalStorage.set(WHATS_NEW_LAST_SEEN_STORAGE_KEY, {
        ...map,
        [buildKey(builderId, userId)]: publishedDateUtc.getTime(),
    });
}

/**
 * Whether anything is newer than the newest date this person has seen. Someone who has never
 * opened the panel sees the dot as long as anything exists.
 */
export function hasUnseenWhatsNew(
    builderId: number,
    userId: number | null,
    latestPublishedDateUtc: Date | null | undefined,
): boolean {
    if (latestPublishedDateUtc == null) return false;

    // No user id means nowhere to record a dismissal, so the dot could never be cleared.
    if (userId == null) return false;

    const lastSeen = getWhatsNewLastSeen(builderId, userId);
    return lastSeen === null || latestPublishedDateUtc.getTime() > lastSeen;
}
